export type SiteTheme = 'device' | 'light' | 'dark';

export const SITE_THEME_KEY = 'mm-theme';

/**
 * One theme for every page that opts in — the storefront and the main page.
 * Stamped on <html> before paint by the inline script below, and again by the
 * switcher. 'device' stamps nothing — the media query is the answer, so a
 * viewer who never chose follows their system without any JS running.
 *
 * Since 2026-08-14 this is the only theme system: the dashboard's tokens
 * (globals.css) ride this same switch; the old cookie-based lib/theme.ts is
 * gone.
 */
export function applyTheme(theme: SiteTheme): void {
  const root = document.documentElement;
  if (theme === 'device') {
    delete root.dataset.mmTheme;
    return;
  }
  root.dataset.mmTheme = theme;
}

export function readTheme(): SiteTheme {
  try {
    const stored = window.localStorage.getItem(SITE_THEME_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'device';
  } catch {
    return 'device';
  }
}

function chooseTheme(theme: SiteTheme): void {
  if (theme === 'device') window.localStorage.removeItem(SITE_THEME_KEY);
  else window.localStorage.setItem(SITE_THEME_KEY, theme);
  applyTheme(theme);
}

/** The stamp a viewer should wear right now: their choice when signed in,
 *  the device otherwise. The boot script makes the same call before paint;
 *  this one re-makes it when the session changes under an open page. */
export function syncTheme(signedIn: boolean): void {
  applyTheme(signedIn ? readTheme() : 'device');
}

/** Light → dark → device → light. Read at the tap, not held in state: the
 *  control draws itself from the `data-mm-theme` stamp (see globals.css). */
export function cycleTheme(): void {
  const next: Record<SiteTheme, SiteTheme> = { light: 'dark', dark: 'device', device: 'light' };
  chooseTheme(next[readTheme()]);
}

// The pre-paint theme boot lives in `public/theme-boot.js` (loaded async from
// the root layout — React 19 warns on inline scripts in components). It reads
// the same SITE_THEME_KEY; change the key in both places together.
